import { Col, Row } from "antd";
import { useForm } from "antd/lib/form/Form";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { branchApi } from "services/branch";
import { serviceApi } from "services/service";

import { authStore } from "store/slices/auth";

import "./Operation.scss";
import OperationHeader from "./components/OperationHeader";
import OperationMap from "./components/OperationMap";
import OperationServiceList from "./components/OperationServiceList";

const Operation = () => {
  const [listOperation, setListOperation] = useState([]);
  const { company_cd } = useSelector(authStore);
  const [activeService, setActiveService] = useState({});
  const [check, setCheck] = useState(false);
  const [listAct, setListAct] = useState([]);
  const [paramFilters, setParamFilters] = useState({});
  const [autoRefreshMinutes, setAutoRefreshMinutes] = useState(0);
  const [branches, setBranches] = useState([]);
  const autoRefreshInterval = useRef(null);
  const [form] = useForm();

  // Auto refresh logic
  useEffect(() => {
    // Clear existing interval
    if (autoRefreshInterval.current) {
      clearInterval(autoRefreshInterval.current);
      autoRefreshInterval.current = null;
    }

    // Set new interval if minutes > 0
    if (autoRefreshMinutes > 0) {
      autoRefreshInterval.current = setInterval(() => {
        onRefetch();
      }, autoRefreshMinutes * 60 * 1000);
    }

    // Cleanup on unmount
    return () => {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
      }
    };
  }, [autoRefreshMinutes, paramFilters]);

  const handleAutoRefreshChange = (value) => {
    setAutoRefreshMinutes(value || 0);
  };

  const fetchBranches = async (officeCds) => {
    if (!officeCds || officeCds.length === 0) {
      setBranches([]);
      return;
    }
    try {
      const res = await branchApi.listByOffices(officeCds);
      setBranches(res.items || []);
    } catch {
      setBranches([]);
    }
  };

  // Listen for branch upload events from the navbar modal (LayoutWrapper).
  // The current paramFilters value must be read at event time, so use a ref.
  const paramFiltersRef = useRef(paramFilters);
  useEffect(() => {
    paramFiltersRef.current = paramFilters;
  }, [paramFilters]);

  useEffect(() => {
    const onBranchUploaded = (e) => {
      const officeCd = e?.detail?.officeCd;
      const selectedOfficeCds = paramFiltersRef.current.office_cds || [];
      if (officeCd && selectedOfficeCds.includes(officeCd)) {
        fetchBranches(selectedOfficeCds);
      }
    };
    window.addEventListener("branch-uploaded", onBranchUploaded);
    return () => window.removeEventListener("branch-uploaded", onBranchUploaded);
  }, []);

  const onSearch = async (searchParams) => {
    const response = await serviceApi.getListOperation({ ...searchParams, company_cd });

    setListOperation(response);
    setActiveService(response.length > 0 ? response[0] : {});
    setParamFilters({ ...searchParams, company_cd });

    // reset data after search
    scrollToTop();
    setCheck(false);
    setListAct([]);

    await fetchBranches(searchParams.office_cds || []);
  };

  const onRefetch = async () => {
    const response = await serviceApi.getListOperation(paramFilters);

    setListOperation(response);

    if (response.length > 0) {
      const found = response.find(service => service.service_no == activeService.service_no);
      setActiveService(found || response[0]);
    } else setActiveService({});

    if (check) {
      const res = await serviceApi.getListActOperation(activeService.service_no);
      setListAct(res);
    }
  };

  const scrollToTop = () => {
    const listOperationElement = document.getElementById("operation-list");
    listOperationElement.scrollTop = 0;
  };

  const handleChangeActive = (data) => {
    if (data.service_no !== activeService.service_no) {
      setActiveService(data);
      setCheck(false);
      setListAct([]);
    }
  };

  const handleToggleCheck = async (service) => {
    if (!check || service.service_no !== activeService.service_no) {
      const res = await serviceApi.getListActOperation(service.service_no);
      setListAct(res);

      if (service.service_no !== activeService.service_no) {
        setActiveService(service);
        if (!check) setCheck(true);
      } else setCheck(true);
    } else {
      setListAct([]);
      setCheck(false);
    }
  };

  return (
    <div id="operation-page">
      <OperationHeader
        form={form}
        numService={listOperation.length}
        onSearch={onSearch}
        autoRefreshMinutes={autoRefreshMinutes}
        onAutoRefreshChange={handleAutoRefreshChange}
      />
      <Row>
        <Col flex={"270px"}>
          <OperationServiceList
            listOperation={listOperation}
            activeService={activeService}
            setActiveService={setActiveService}
            onChangeActive={handleChangeActive}
            onToggleCheck={handleToggleCheck}
            check={check}
          />
        </Col>
        <Col flex={"auto"}>
          <OperationMap
            listAct={listAct}
            refetch={onRefetch}
            activeService={activeService}
            unActiveService={listOperation.filter(
              (opration) => opration.service_no !== activeService.service_no)
            }
            onChangeActive={handleChangeActive}
            autoRefreshMinutes={autoRefreshMinutes}
            branches={branches}
          />
        </Col>
      </Row>
    </div>
  );
};

export default Operation;
